import React, { useState, useEffect } from 'react';
import './AddTaskPopup.css';
import { TextField, Button, Typography, IconButton, LinearProgress, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { Close as CloseIcon, AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, FirebaseStorage } from 'firebase/storage'; // Corrected import for FirebaseStorage
import { app } from './firebase.ts'; // Assuming firebase.js exports the initialized app
import { useTheme } from './ThemeContext.tsx'; // Import useTheme
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth'; // Import getAuth and onAuthStateChanged
import { addDays, setDate, setDay, getDay, isSameDay, addMonths, startOfDay } from 'date-fns'; // Corrected imports for date-fns and startOfDay
import { SelectChangeEvent } from '@mui/material';
import { Checklist } from './ChecklistsPage.tsx'; // Import Checklist interface

interface AddTaskPopupProps {
  isOpen: boolean;
  onClose: () => void;
  checklists: Checklist[];
  selectedChecklist: Checklist | null;
  onAddTask: (task: {
    name: string;
    description: string;
    videoUrl: string;
    checklistId: string;
    isRecurring: boolean;
    recurrence: { type: string; details: any };
    nextOccurrenceDates: Date[] | null;
  }) => Promise<void>;
}

function AddTaskPopup({ isOpen, onClose, checklists, selectedChecklist, onAddTask }: AddTaskPopupProps) {
  const [taskName, setTaskName] = useState<string>('');
  const [taskDescription, setTaskDescription] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [storageInstance, setStorageInstance] = useState<FirebaseStorage | null>(null); // Renamed to avoid conflict
  const [aiSuggestedDescription, setAiSuggestedDescription] = useState<string>('');
  const [showAiSuggestion, setShowAiSuggestion] = useState<boolean>(false);
  const [isRetouching, setIsRetouching] = useState<boolean>(false);
  const [retouchError, setRetouchError] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [recurrenceType, setRecurrenceType] = useState<string>('none');
  const [weeklyDays, setWeeklyDays] = useState<number[]>([]);
  const [monthlyType, setMonthlyType] = useState<string>('dayOfMonth');
  const [monthlyDayOfMonth, setMonthlyDayOfMonth] = useState<number>(1);
  const [monthlyNth, setMonthlyNth] = useState<string>('first');
  const [monthlyDayOfWeek, setMonthlyDayOfWeek] = useState<string>('Monday');
  const [currentUser, setCurrentUser] = useState<User | null>(null); // State to hold the current authenticated user
  const [authLoading, setAuthLoading] = useState<boolean>(true); // State to track auth loading

  const { theme } = useTheme();

  useEffect(() => {
    if (app) {
      setStorageInstance(getStorage(app));
      const auth = getAuth(app);
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setAuthLoading(false);
      });
      return () => unsubscribe(); // Cleanup subscription on unmount
    }
  }, [app]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setUploadError(null);
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleRetouchDescription = async () => {
    if (!taskDescription.trim()) {
      setRetouchError('Please enter a description to retouch.');
      return;
    }

    setIsRetouching(true);
    setRetouchError(null);
    setShowAiSuggestion(false);

    try {
      const functions = getFunctions(app);
      const retouchFunction = httpsCallable<{ description: string, language: string }, { rewrittenDescription: string }>(functions, 'retouchTaskDescription');
      const userLanguage = navigator.language.split('-')[0];
      const result = await retouchFunction({ description: taskDescription, language: userLanguage });
      
      if (result.data && result.data.rewrittenDescription) {
        setAiSuggestedDescription(result.data.rewrittenDescription);
        setShowAiSuggestion(true);
      } else {
        setRetouchError('AI did not return a valid suggestion.');
      }
    } catch (error: any) { // Explicitly type error as any
      console.error('Error retouching description:', error);
      setRetouchError(`Failed to retouch description: ${error.message}`);
    } finally {
      setIsRetouching(false);
    }
  };

  const handleConfirmAiSuggestion = () => {
    setTaskDescription(aiSuggestedDescription);
    setShowAiSuggestion(false);
    setAiSuggestedDescription('');
  };

  const handleDenyAiSuggestion = () => {
    setShowAiSuggestion(false);
    setAiSuggestedDescription('');
  };

  const handleWeeklyDayChange = (dayIndex: number) => {
    setWeeklyDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex].sort((a, b) => a - b)
    );
  };

  const calculateNextOccurrence = (): Date[] | null => {
    const today = startOfDay(new Date());
    let nextDates: Date[] = [];

    if (recurrenceType === 'daily') {
      nextDates.push(addDays(today, 1));
    } else if (recurrenceType === 'weekly' || recurrenceType === 'biweekly') {
      if (weeklyDays.length === 0) return null;

      const sortedWeeklyDays = weeklyDays.sort((a, b) => a - b);
      const currentDay = getDay(today);

      for (const day of sortedWeeklyDays) {
        let potentialDate = setDay(today, day);
        if (potentialDate < today) { // If the day has passed this week
          potentialDate = addDays(potentialDate, 7);
        }
        if (isSameDay(potentialDate, today)) { // If today is the selected day, next occurrence is next week
          potentialDate = addDays(potentialDate, 7);
        }
        if (recurrenceType === 'biweekly') {
          potentialDate = addDays(potentialDate, 7);
        }
        nextDates.push(potentialDate);
      }
    } else if (recurrenceType === 'monthly') {
      if (monthlyType === 'dayOfMonth') {
        let potentialDate = setDate(today, monthlyDayOfMonth);
        if (potentialDate < today) {
          potentialDate = addMonths(potentialDate, 1);
        }
        nextDates.push(potentialDate);
      } else if (monthlyType === 'nthDay') {
        const nthMap: { [key: string]: number } = { 'first': 1, 'second': 2, 'third': 3, 'fourth': 4, 'last': -1 };
        const dayOfWeekMap: { [key: string]: number } = {
          'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
          'Thursday': 4, 'Friday': 5, 'Saturday': 6
        };
        const targetNth = nthMap[monthlyNth];
        const targetDay = dayOfWeekMap[monthlyDayOfWeek];

        let currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        let found = false;
        for (let i = 0; i < 2; i++) {
          let count = 0;
          let tempDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
          while (tempDate.getMonth() === currentMonth.getMonth()) {
            if (getDay(tempDate) === targetDay) {
              count++;
              if (targetNth === -1) {
                let nextTempDate = addDays(tempDate, 7);
                if (nextTempDate.getMonth() !== currentMonth.getMonth()) {
                  if (tempDate >= today) {
                    nextDates.push(tempDate);
                    found = true;
                    break;
                  }
                }
              } else if (count === targetNth) {
                if (tempDate >= today) {
                  nextDates.push(tempDate);
                  found = true;
                  break;
                }
              }
            }
            tempDate = addDays(tempDate, 1);
          }
          if (found) break;
          currentMonth = addMonths(currentMonth, 1);
          currentMonth.setDate(1);
        }
        if (!found && nextDates.length === 0) {
          let nextMonth = addMonths(today, 1);
          nextMonth.setDate(1);
          let count = 0;
          let tempDate = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
          while (tempDate.getMonth() === nextMonth.getMonth()) {
            if (getDay(tempDate) === targetDay) {
              count++;
              if (targetNth === -1) {
                let nextTempDate = addDays(tempDate, 7);
                if (nextTempDate.getMonth() !== nextMonth.getMonth()) {
                  nextDates.push(tempDate);
                  break;
                }
              } else if (count === targetNth) {
                nextDates.push(tempDate);
                break;
              }
            }
            tempDate = addDays(tempDate, 1);
          }
        }
      }
    }
    return nextDates.length > 0 ? nextDates : null;
  };

  const handleSaveTask = async () => {
    if (!selectedChecklist) {
      setUploadError('Please select a checklist.');
      return;
    }

    let videoUrl: string = '';
    let recurrenceDetails: any = {};
    let nextOccurrenceDates: Date[] | null = null;

    if (isRecurring) {
      if (recurrenceType === 'none') {
        setUploadError('Please select a recurrence type (Daily, Weekly, or Monthly).');
        return;
      }
      if (recurrenceType === 'weekly' || recurrenceType === 'biweekly') {
        if (weeklyDays.length === 0) {
          setUploadError('Please select at least one day for weekly/biweekly recurrence.');
          return;
        }
        recurrenceDetails = { days: weeklyDays };
      } else if (recurrenceType === 'monthly') {
        if (monthlyType === 'dayOfMonth') {
          recurrenceDetails = { type: 'dayOfMonth', value: monthlyDayOfMonth };
        } else if (monthlyType === 'nthDay') {
          recurrenceDetails = { type: 'nthDay', nth: monthlyNth, day: monthlyDayOfWeek };
        }
      }
      nextOccurrenceDates = calculateNextOccurrence();
      if (!nextOccurrenceDates) {
        setUploadError('Could not calculate next occurrence date(s). Please check recurrence settings.');
        return;
      }
    }

    const taskPayload = {
      name: taskName,
      description: taskDescription,
      videoUrl: '',
      checklistId: selectedChecklist.id,
      isRecurring: isRecurring,
      recurrence: {
        type: recurrenceType,
        details: recurrenceDetails,
      },
      nextOccurrenceDates: nextOccurrenceDates,
    };

    if (selectedFile) {
      if (!storageInstance) {
        setUploadError('Firebase Storage not initialized. Please try again.');
        return;
      }
      console.log('Firebase Storage instance:', storageInstance);
      if (!currentUser) {
        setUploadError('You must be logged in to upload videos.');
        return;
      }
      setIsUploading(true);
      setUploadError(null);
      const storageRef = ref(storageInstance, `videos/${currentUser.uid}/${Date.now()}_${selectedFile.name}`);

      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error: any) => {
          console.error('Upload failed:', error);
          setUploadError('Failed to upload video. Please try again.');
          setIsUploading(false);
        },
        async () => {
          taskPayload.videoUrl = await getDownloadURL(uploadTask.snapshot.ref);
          setIsUploading(false);
          onAddTask(taskPayload);
          onClose();
        }
      );
    } else {
      onAddTask(taskPayload);
      onClose();
    }
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const nthOptions = ['first', 'second', 'third', 'fourth', 'last'];

  return (
    <Box className="add-task-popup" sx={{ backgroundColor: `${theme.colors.lightGrayBackground}E6` }}>
      <Box className="add-task-popup-content" sx={{ backgroundColor: theme.colors.whiteCardBackground, color: theme.colors.darkText }}>
        <Box className="add-task-popup-header">
          <Typography variant="h6" sx={{ color: theme.colors.darkText }}>Add New Task</Typography>
          <IconButton onClick={onClose} sx={{ color: theme.colors.lightText }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          label="Task Description *"
          variant="outlined"
          fullWidth
          margin="normal"
          value={taskName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaskName(e.target.value)}
          required
          InputLabelProps={{
            sx: { color: theme.colors.lightText }
          }}
          InputProps={{
            sx: { color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }
          }}
        />
        <TextField
          label="Detailed Description (Optional)"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={taskDescription}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTaskDescription(e.target.value)}
          InputLabelProps={{
            sx: { color: theme.colors.lightText }
          }}
          InputProps={{
            sx: { color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={isRecurring}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setIsRecurring(e.target.checked);
                if (!e.target.checked) {
                  setRecurrenceType('none');
                  setWeeklyDays([]);
                  setMonthlyType('dayOfMonth');
                  setMonthlyDayOfMonth(1);
                  setMonthlyNth('first');
                  setMonthlyDayOfWeek('Monday');
                }
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.colors.primaryRed, /* Use primaryRed for the switch thumb */
                  '&:hover': {
                    backgroundColor: 'rgba(255, 77, 77, 0.08)', /* Subtle hover effect for red */
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.colors.primaryRed, /* Use primaryRed for the switch track */
                  opacity: 0.5, /* Keep opacity for the track */
                },
              }}
            />
          }
          label={<Typography sx={{ color: theme.colors.darkText }}>Make this a recurring task</Typography>}
          sx={{ mt: 1, mb: 2 }}
        />

        {isRecurring && (
          <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
            <InputLabel sx={{ color: theme.colors.lightText }}>Recurrence</InputLabel>
            <Select
              value={recurrenceType}
              label="Recurrence"
              onChange={(e: SelectChangeEvent<string>) => setRecurrenceType(e.target.value)}
              sx={{ color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="biweekly">Biweekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        )}

        {isRecurring && (recurrenceType === 'weekly' || recurrenceType === 'biweekly') && (
          <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ color: theme.colors.darkText, mb: 1 }}>Select Day(s) of Week:</Typography>
            <FormGroup row>
              {daysOfWeek.map((day, index) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={weeklyDays.includes(index)}
                      onChange={() => handleWeeklyDayChange(index)}
                      sx={{ color: theme.colors.lightText }}
                    />
                  }
                  label={<Typography sx={{ color: theme.colors.darkText }}>{day}</Typography>}
                />
              ))}
            </FormGroup>
          </FormControl>
        )}

        {isRecurring && recurrenceType === 'monthly' && (
          <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ color: theme.colors.darkText, mb: 1 }}>Monthly Recurrence:</Typography>
            <Select
              value={monthlyType}
              label="Monthly Type"
              onChange={(e: SelectChangeEvent<string>) => setMonthlyType(e.target.value)}
              sx={{ color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground, mb: 2 }}
            >
              <MenuItem value="dayOfMonth">Day of Month</MenuItem>
              <MenuItem value="nthDay">Nth Day of Week</MenuItem>
            </Select>

            {monthlyType === 'dayOfMonth' && (
              <TextField
                label="Day of Month (1-31)"
                type="number"
                variant="outlined"
                fullWidth
                value={monthlyDayOfMonth}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMonthlyDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                InputLabelProps={{
                  sx: { color: theme.colors.lightText }
                }}
                InputProps={{
                  sx: { color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }
                }}
              />
            )}

            {monthlyType === 'nthDay' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel sx={{ color: theme.colors.lightText }}>Nth</InputLabel>
                  <Select
                    value={monthlyNth}
                    label="Nth"
                    onChange={(e: SelectChangeEvent<string>) => setMonthlyNth(e.target.value)}
                    sx={{ color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }}
                  >
                    {nthOptions.map(option => (
                      <MenuItem key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel sx={{ color: theme.colors.lightText }}>Day</InputLabel>
                  <Select
                    value={monthlyDayOfWeek}
                    label="Day"
                    onChange={(e: SelectChangeEvent<string>) => setMonthlyDayOfWeek(e.target.value)}
                    sx={{ color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }}
                  >
                    {daysOfWeek.map(day => (
                      <MenuItem key={day} value={day}>{day}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </FormControl>
        )}

        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={handleRetouchDescription}
          disabled={isRetouching || !taskDescription.trim() || authLoading || !currentUser} // Disable if not authenticated
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: '#8A2BE2', /* Purple color for Retouch button */
            borderColor: '#8A2BE2',
            color: '#FFFFFF', /* White text */
            '&:hover': {
              backgroundColor: '#7A1ED1', /* Slightly darker purple on hover */
              borderColor: '#7A1ED1',
            },
          }}
        >
          Retouch Description with AI
        </Button>

        {authLoading && (
          <Typography variant="body2" sx={{ mt: 1, mb: 2, color: theme.colors.lightText }}>
            Checking authentication status...
          </Typography>
        )}
        {!currentUser && !authLoading && (
          <Typography color="error" variant="body2" sx={{ mt: 1, mb: 2, color: theme.colors.primaryRed }}>
            You must be logged in to use the AI retouch feature.
          </Typography>
        )}

        {isRetouching && (
          <LinearProgress sx={{ mt: 2, mb: 2, backgroundColor: theme.colors.borderColor, '& .MuiLinearProgress-bar': { backgroundColor: theme.colors.primaryRed } }} />
        )}
        {retouchError && (
          <Typography color="error" variant="body2" sx={{ mt: 1, mb: 2, color: theme.colors.primaryRed }}>
            {retouchError}
          </Typography>
        )}

        {showAiSuggestion && (
          <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.colors.borderColor}`, borderRadius: '4px', backgroundColor: theme.colors.lightGrayBackground }}>
            <Typography variant="subtitle2" sx={{ color: theme.colors.darkText, mb: 1 }}>AI Suggestion:</Typography>
            <Typography variant="body2" sx={{ color: theme.colors.darkText, whiteSpace: 'pre-wrap' }}>
              {aiSuggestedDescription}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleDenyAiSuggestion}
                sx={{
                  backgroundColor: theme.colors.whiteCardBackground,
                  borderColor: theme.colors.borderColor,
                  color: theme.colors.darkText,
                }}
              >
                Deny
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmAiSuggestion}
                sx={{
                  backgroundColor: theme.colors.primaryRed,
                  color: theme.colors.whiteCardBackground,
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}

        <Box className="add-task-popup-file-upload">
          <Button
            variant="contained" /* Changed to contained for solid background */
            component="label"
            disabled={isUploading}
            sx={{
              backgroundColor: '#F5F5F5', /* Light gray for Upload Video button */
              color: '#424242', /* Darker text */
              boxShadow: 'none', /* Remove shadow */
              '&:hover': {
                backgroundColor: '#E0E0E0', /* Slightly darker gray on hover */
              },
            }}
          >
            Upload Video
            <input type="file" hidden accept="video/*" onChange={handleFileChange} />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ ml: 2, color: theme.colors.lightText }}>
              {selectedFile.name}
            </Typography>
          )}
        </Box>
        {isUploading && (
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2, backgroundColor: theme.colors.borderColor, '& .MuiLinearProgress-bar': { backgroundColor: theme.colors.primaryRed } }} />
        )}
        {uploadError && (
          <Typography color="error" variant="body2" sx={{ mt: 1, color: theme.colors.primaryRed }}>
            {uploadError}
          </Typography>
        )}
        <Box className="add-task-popup-buttons">
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isUploading}
            sx={{
              backgroundColor: '#E0E0E0', /* Light gray for Cancel button */
              color: '#424242', /* Darker text */
              boxShadow: 'none', /* Remove shadow */
              '&:hover': {
                backgroundColor: '#CCCCCC', /* Slightly darker gray on hover */
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTask}
            disabled={isUploading}
            sx={{
              backgroundColor: '#4285F4', /* Blue color for Save Task */
              color: '#FFFFFF', /* White text */
              boxShadow: 'none', /* Remove shadow */
              '&:hover': {
                backgroundColor: '#3367D6', /* Slightly darker blue on hover */
              },
            }}
          >
            Save Task
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AddTaskPopup;
